import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useSpring, useTransform } from 'framer-motion';
import { ShieldCheck, Pill as PillIcon, FlaskConical, Ban, AlertTriangle, CheckCircle } from 'lucide-react';
import { getBulkToggleCount, bulkToggleCreditPermission, getBulkToggleJobStatus } from '../../services/creditLineService';

// Same identity-color language as the per-subscriber CreditPermissionToggles
// (Medicine = sky, Diagnostic = violet, Both = amber) so the two surfaces
// read as one system.
const SCOPE_COLOR = {
  medicine: {
    cardOn: 'bg-gradient-to-br from-sky-50 to-white border-sky-200 hover:shadow-[0_4px_16px_-6px_rgba(14,165,233,0.35)]',
    cardOff: 'bg-slate-50 border-slate-200 hover:border-slate-300',
    iconWrapOn: 'bg-sky-500 text-white shadow-sm shadow-sky-500/30',
    iconWrapOff: 'bg-slate-200 text-slate-400',
    accentText: 'text-sky-600',
    track: '#0ea5e9',
  },
  diagnostic: {
    cardOn: 'bg-gradient-to-br from-violet-50 to-white border-violet-200 hover:shadow-[0_4px_16px_-6px_rgba(139,92,246,0.35)]',
    cardOff: 'bg-slate-50 border-slate-200 hover:border-slate-300',
    iconWrapOn: 'bg-violet-500 text-white shadow-sm shadow-violet-500/30',
    iconWrapOff: 'bg-slate-200 text-slate-400',
    accentText: 'text-violet-600',
    track: '#8b5cf6',
  },
  both: {
    cardOn: 'bg-gradient-to-br from-amber-50 to-white border-amber-200 hover:shadow-[0_4px_16px_-6px_rgba(245,158,11,0.35)]',
    cardOff: 'bg-slate-50 border-slate-200 hover:border-slate-300',
    iconWrapOn: 'bg-amber-500 text-white shadow-sm shadow-amber-500/30',
    iconWrapOff: 'bg-slate-200 text-slate-400',
    accentText: 'text-amber-600',
    track: '#f59e0b',
  },
};

const SCOPES = [
  { scope: 'medicine', label: 'Medicine', icon: PillIcon },
  { scope: 'diagnostic', label: 'Diagnostic', icon: FlaskConical },
  { scope: 'both', label: 'Both', icon: Ban },
];

const CONFIRM_TEXT = 'CONFIRM';
const POLL_INTERVAL_MS = 1500;

// A bulk action has no single "current" on/off state to show — it acts on
// many subscribers who can individually be enabled or disabled. A toggle
// switch here would be misleading (what would it even reflect on refresh?),
// so this is a one-shot command button, not a persistent setting: Enable or
// Disable, each opening the same count + typed-confirm + progress flow.
const BulkActionButton = ({ scope, enabled, onClick, label, icon: Icon }) => {
  const c = SCOPE_COLOR[scope];
  return (
    <motion.button
      type="button"
      onClick={onClick}
      whileTap={{ scale: 0.98 }}
      className={`relative flex items-center gap-3 w-full rounded-2xl px-4 py-3 border text-left overflow-hidden transition-all duration-300 cursor-pointer ${
        enabled ? c.cardOn : c.cardOff
      }`}
    >
      <span className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors duration-300 ${enabled ? c.iconWrapOn : c.iconWrapOff}`}>
        <Icon className="w-4 h-4" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-gray-900 truncate">{label}</span>
        <span className={`block text-[11px] font-semibold ${enabled ? c.accentText : 'text-slate-500'}`}>
          {enabled ? 'Enable for all' : 'Disable for all'}
        </span>
      </span>
    </motion.button>
  );
};

const AnimatedCounter = ({ value }) => {
  const spring = useSpring(0, { stiffness: 90, damping: 20 });
  const rounded = useTransform(spring, (v) => Math.round(v).toLocaleString('en-IN'));
  useEffect(() => { spring.set(value); }, [value, spring]);
  return <motion.span>{rounded}</motion.span>;
};

const ProgressBar = ({ status }) => {
  const total = status?.total ?? 0;
  const processed = status?.processed ?? 0;
  const failed = status?.failed ?? 0;
  const percent = total > 0 ? Math.min(100, (processed / total) * 100) : 0;
  const isRunning = status?.status === 'running';
  const isFailed = status?.status === 'failed';
  const done = status?.status === 'complete' || isFailed;
  const barColor = isFailed ? 'bg-amber-500' : 'bg-emerald-500';

  return (
    <div className="space-y-2.5">
      <div className="flex items-center justify-between text-xs">
        <span className="font-medium text-gray-700 tabular-nums">
          <AnimatedCounter value={processed} /> / {total.toLocaleString('en-IN')} processed
        </span>
        <AnimatePresence>
          {failed > 0 && (
            <motion.span
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              className="flex items-center gap-1 text-amber-600 font-medium"
            >
              <AlertTriangle className="w-3 h-3" />
              {failed} failed
            </motion.span>
          )}
        </AnimatePresence>
      </div>

      <div className="relative h-3 rounded-full bg-gray-100 overflow-hidden">
        <motion.div
          className={`relative h-full rounded-full ${barColor} overflow-hidden`}
          initial={{ width: 0 }}
          animate={{ width: `${percent}%` }}
          transition={{ type: 'spring', stiffness: 100, damping: 22 }}
        >
          {isRunning && (
            <motion.div
              className="absolute inset-0"
              style={{
                backgroundImage: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.55) 50%, transparent 70%)',
                backgroundSize: '200% 100%',
              }}
              animate={{ backgroundPositionX: ['150%', '-50%'] }}
              transition={{ duration: 1.1, repeat: Infinity, ease: 'linear' }}
            />
          )}
        </motion.div>
        {isRunning && (
          <motion.div
            className="absolute inset-y-0 left-0 w-1/4 bg-white/25 blur-sm"
            animate={{ x: ['-100%', '400%'] }}
            transition={{ duration: 1.6, repeat: Infinity, ease: 'easeInOut' }}
          />
        )}
      </div>

      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold text-gray-400 tabular-nums">
          {Math.round(percent)}%
        </span>
        <AnimatePresence mode="wait">
          {done ? (
            <motion.p
              key="done"
              initial={{ opacity: 0, scale: 0.7, y: 4 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              transition={{ type: 'spring', stiffness: 400, damping: 18 }}
              className={`flex items-center gap-1.5 text-xs font-semibold ${isFailed ? 'text-amber-600' : 'text-emerald-600'}`}
            >
              <motion.span
                initial={{ rotate: -90, scale: 0.5 }}
                animate={{ rotate: 0, scale: 1 }}
                transition={{ type: 'spring', stiffness: 500, damping: 20, delay: 0.05 }}
              >
                <CheckCircle className="w-3.5 h-3.5" />
              </motion.span>
              {isFailed ? 'Job stopped with an error' : 'Complete'}
            </motion.p>
          ) : (
            <motion.p
              key="running"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="flex items-center gap-1.5 text-xs font-medium text-gray-400"
            >
              <motion.span
                className="flex gap-0.5"
              >
                {[0, 1, 2].map((i) => (
                  <motion.span
                    key={i}
                    className="w-1 h-1 rounded-full bg-gray-400"
                    animate={{ opacity: [0.3, 1, 0.3] }}
                    transition={{ duration: 1, repeat: Infinity, delay: i * 0.15, ease: 'easeInOut' }}
                  />
                ))}
              </motion.span>
              Processing
            </motion.p>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
};

const BulkToggleModal = ({ pending, onClose, currentRole }) => {
  const [count, setCount] = useState(null);
  const [countLoading, setCountLoading] = useState(true);
  const [countError, setCountError] = useState('');
  const [typedConfirm, setTypedConfirm] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');
  const [jobId, setJobId] = useState(null);
  const [jobStatus, setJobStatus] = useState(null);
  const pollRef = useRef(null);
  const closeTimerRef = useRef(null);

  useEffect(() => {
    if (!pending) return;
    setCount(null);
    setCountError('');
    setTypedConfirm('');
    setSubmitError('');
    setJobId(null);
    setJobStatus(null);
    setCountLoading(true);
    getBulkToggleCount(currentRole, pending.scope)
      .then((res) => {
        if (!res?.metadata?.api_status) throw new Error('Failed to load affected subscriber count');
        setCount(res.msg?.count ?? 0);
      })
      .catch((err) => setCountError(err.message || 'Failed to load affected subscriber count'))
      .finally(() => setCountLoading(false));
  }, [pending, currentRole]);

  useEffect(() => {
    if (!jobId) return undefined;
    const poll = async () => {
      try {
        const res = await getBulkToggleJobStatus(currentRole, jobId);
        if (res?.metadata?.api_status) {
          setJobStatus(res.msg);
          if (res.msg?.status !== 'running' && pollRef.current) {
            clearInterval(pollRef.current);
            pollRef.current = null;
          }
        }
      } catch {
        // transient poll failure — keep polling, next tick may succeed
      }
    };
    poll();
    pollRef.current = setInterval(poll, POLL_INTERVAL_MS);
    return () => {
      if (pollRef.current) clearInterval(pollRef.current);
      pollRef.current = null;
    };
  }, [jobId, currentRole]);

  const isDone = jobId && jobStatus && jobStatus.status !== 'running';

  useEffect(() => {
    if (!isDone) return undefined;
    // Brief pause so the admin sees the completed bar/status before the
    // modal disappears on its own — an instant close would hide a failed
    // outcome just as easily as a successful one.
    closeTimerRef.current = setTimeout(() => onClose(), 1200);
    return () => {
      if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
      closeTimerRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDone]);

  if (!pending) return null;

  const handleConfirm = async () => {
    setSubmitting(true);
    setSubmitError('');
    try {
      const res = await bulkToggleCreditPermission(currentRole, pending.scope, pending.enabled);
      if (!res?.metadata?.api_status) throw new Error(typeof res?.msg === 'string' ? res.msg : 'Failed to queue bulk update');
      setJobId(res.msg?.job_id);
    } catch (err) {
      setSubmitError(err.message || 'Failed to queue bulk update');
    } finally {
      setSubmitting(false);
    }
  };

  const actionLabel = pending.enabled ? 'Enable' : 'Disable';
  const actionColor = pending.enabled ? 'emerald' : 'red';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95, y: 8 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        transition={{ type: 'spring', stiffness: 380, damping: 28 }}
        className="bg-white rounded-2xl shadow-xl w-full max-w-md p-6 space-y-4"
      >
        <div>
          <p className={`text-[10px] font-semibold uppercase tracking-widest mb-1 ${actionColor === 'emerald' ? 'text-emerald-500' : 'text-red-500'}`}>
            Credit Line — Bulk Action
          </p>
          <h3 className="text-base font-semibold text-gray-900">{actionLabel} {pending.label} (All)</h3>
        </div>

        {jobId ? (
          <>
            <ProgressBar status={jobStatus} />
            {isDone && (
              <div className="flex justify-end pt-2">
                <button
                  type="button"
                  onClick={() => onClose(true)}
                  className="px-4 py-2 text-sm rounded-lg bg-[#0F4C81] text-white hover:bg-[#0C3E6B]"
                >
                  Close
                </button>
              </div>
            )}
          </>
        ) : (
          <>
            <p className="text-sm text-gray-500">
              This will {pending.enabled ? 'enable' : 'disable'} {pending.scope === 'both' ? 'medicine and diagnostic' : pending.scope} credit line usage for{' '}
              {countLoading ? '…' : countError ? 'an unknown number of' : (count ?? 0).toLocaleString('en-IN')} subscriber
              {count === 1 ? '' : 's'} with an active credit line account.
            </p>
            {countError && <p className="text-xs text-red-600">{countError}</p>}
            {submitError && <p className="text-xs text-red-600">{submitError}</p>}

            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Type <span className="font-mono font-semibold text-gray-800">{CONFIRM_TEXT}</span> to proceed
              </label>
              <input
                type="text"
                value={typedConfirm}
                onChange={(e) => setTypedConfirm(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                autoComplete="off"
              />
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <button
                type="button"
                onClick={() => onClose(false)}
                disabled={submitting}
                className="px-4 py-2 text-sm rounded-lg border border-gray-200 text-gray-600 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleConfirm}
                disabled={submitting || countLoading || Boolean(countError) || typedConfirm !== CONFIRM_TEXT}
                className={`px-4 py-2 text-sm rounded-lg text-white disabled:opacity-50 disabled:cursor-not-allowed ${
                  actionColor === 'emerald' ? 'bg-emerald-600 hover:bg-emerald-700' : 'bg-red-600 hover:bg-red-700'
                }`}
              >
                {submitting ? 'Queuing…' : 'Confirm'}
              </button>
            </div>
          </>
        )}
      </motion.div>
    </div>
  );
};

// Bulk credit-line permission actions for the Subscriber module list page
// (SubscriberList.jsx). Applies to ALL subscribers with an active credit
// line account — not scoped to the current page/filter. Each button is a
// one-shot command (Enable/Disable for all), not a persistent toggle —
// there is no single per-scope state to represent across many subscribers.
// Visibility gate: SuperAdmin only.
const CreditBulkActions = ({ isSuperAdmin, currentRole }) => {
  const [pending, setPending] = useState(null);
  const [open, setOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    const onDocMouseDown = (e) => menuRef.current && !menuRef.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  if (!isSuperAdmin) return null;

  const requestAction = (s, enabled) => {
    setPending({ ...s, enabled });
    setOpen(false);
  };

  return (
    <>
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-haspopup="menu"
          aria-expanded={open}
          className={`inline-flex items-center gap-2 pl-2.5 pr-3 py-2 text-xs font-semibold rounded-xl border transition-all ${
            open
              ? 'bg-[#0F4C81]/5 border-[#0F4C81]/30 text-[#0F4C81] shadow-sm'
              : 'bg-white border-gray-200 text-gray-600 hover:bg-gray-50 hover:border-gray-300'
          }`}
        >
          <span className="w-5 h-5 rounded-lg bg-[#0F4C81]/10 text-[#0F4C81] flex items-center justify-center shrink-0">
            <ShieldCheck className="w-2.5 h-2.5" />
          </span>
          Credit Actions
        </button>

        <AnimatePresence>
          {open && (
            <motion.div
              initial={{ opacity: 0, y: -6, scale: 0.97 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -6, scale: 0.97 }}
              transition={{ duration: 0.15 }}
              role="menu"
              className="absolute right-0 mt-2 w-[28rem] bg-white rounded-2xl border border-gray-200 shadow-lg shadow-black/5 overflow-hidden z-30 p-3 space-y-3"
            >
              <div className="px-1">
                <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-widest">Credit Line — Bulk Action</p>
                <p className="text-[11px] text-gray-400 mt-0.5">Applies only to subscribers with an active credit line account.</p>
              </div>

              {SCOPES.map((s) => (
                <div key={s.scope} className="grid grid-cols-2 gap-2">
                  <BulkActionButton scope={s.scope} icon={s.icon} label={s.label} enabled onClick={() => requestAction(s, true)} />
                  <BulkActionButton scope={s.scope} icon={s.icon} label={s.label} enabled={false} onClick={() => requestAction(s, false)} />
                </div>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <BulkToggleModal pending={pending} currentRole={currentRole} onClose={() => setPending(null)} />
    </>
  );
};

export default CreditBulkActions;
